import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./AddSection.css";
import SectionModal from "./SectionModal";
import { generateProductDescriptionStream } from "../../utils/Product";
import { toast } from "react-toastify";

const AddSection = ({
  onAddSection,
  previewMode,
  initialSku,
  keywords,
  agentId,
  sessionId,
  language,
  locationFocus,
  furtherDetails,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [responseData, setResponseData] = useState(null);

  // Simplified form data structure
  const [formData, setFormData] = useState({
    title: "",
    layout_type: "",
    column1: null,
    column2: null,
    column3: null,
    column4: null,
    column5: null,
    column6: null,
    card: null,
  });

  const layoutTypeToCount = {
    one_column: 1,
    two_columns: 2,
    three_columns: 3,
    four_columns: 4,
    five_columns: 5,
    six_columns: 6,
  };

  // Function to structure form data for API call
  const getStructuredFormData = () => {
    const structuredFormData = {
      title: formData.title,
      layout_type: formData.layout_type,
    };

    // Add columns based on layout type
    if (formData.layout_type === "one_column" && formData.column1) {
      structuredFormData.column1 = formData.column1;
    } else if (formData.layout_type === "two_columns") {
      if (formData.column1) structuredFormData.column1 = formData.column1;
      if (formData.column2) structuredFormData.column2 = formData.column2;
    } else if (formData.layout_type === "three_columns") {
      if (formData.column1) structuredFormData.column1 = formData.column1;
      if (formData.column2) structuredFormData.column2 = formData.column2;
      if (formData.column3) structuredFormData.column3 = formData.column3;
    } else if (formData.layout_type === "four_columns") {
      if (formData.column1) structuredFormData.column1 = formData.column1;
      if (formData.column2) structuredFormData.column2 = formData.column2;
      if (formData.column3) structuredFormData.column3 = formData.column3;
      if (formData.column4) structuredFormData.column4 = formData.column4;
    } else if (formData.layout_type === "five_columns") {
      if (formData.column1) structuredFormData.column1 = formData.column1;
      if (formData.column2) structuredFormData.column2 = formData.column2;
      if (formData.column3) structuredFormData.column3 = formData.column3;
      if (formData.column4) structuredFormData.column4 = formData.column4;
      if (formData.column5) structuredFormData.column5 = formData.column5;
    } else if (formData.layout_type === "six_columns") {
      if (formData.column1) structuredFormData.column1 = formData.column1;
      if (formData.column2) structuredFormData.column2 = formData.column2;
      if (formData.column3) structuredFormData.column3 = formData.column3;
      if (formData.column4) structuredFormData.column4 = formData.column4;
      if (formData.column5) structuredFormData.column5 = formData.column5;
      if (formData.column6) structuredFormData.column6 = formData.column6;
    } else if (formData.layout_type === "card_layout" && formData.card) {
      structuredFormData.card = formData.card;
    }

    return structuredFormData;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!initialSku || !agentId || !sessionId) {
      toast.error("Missing required data for API call");
      return;
    }

    setIsGenerating(true);

    try {
      // Get structured form data
      const structuredFormData = getStructuredFormData();

      console.log(formData.layout_type);

      let responseData = null;
      let error = null;

      const response = await generateProductDescriptionStream(
        initialSku,
        keywords || [],
        "custom_section", // section_name
        furtherDetails,
        agentId,
        sessionId,
        (chunk) => {
          responseData = chunk;
        },
        () => {},
        (err) => {
          error = err;
        },
        null,
        language?.label || language,
        locationFocus?.label || locationFocus,
        null,
        structuredFormData
      );
      // The response is an array of accumulated data, get the last meaningful response
      if (response && Array.isArray(response) && response.length > 0) {
        responseData = response[0];
      } else {
        responseData = response;
      }

      setResponseData(responseData);
      setShowResponse(true);
      toast.success("Custom section generated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to generate custom section");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddToDocument = () => {
    // Parse the response data and create appropriate section objects
    const sections = parseResponseToSections(
      responseData,
      formData.layout_type,
      formData.title
    );

    // Add each section to the document
    sections.forEach((section) => {
      onAddSection(section);
    });

    setShowCustomModal(false);
    setShowResponse(false);
    setResponseData(null);
    // Reset form data
    setFormData({
      title: "",
      layout_type: "",
      column1: null,
      column2: null,
      column3: null,
      column4: null,
      column5: null,
      column6: null,
      card: null,
    });
  };

  const parseResponseToSections = (responseData, layoutType, userTitle) => {
    const sections = [];

    // Use user-provided title or fallback to default
    const sectionTitle =
      userTitle && userTitle.trim() ? userTitle.trim() : "Custom Section";

    try {
      // Handle different response formats
      let parsedData = null;

      // Try to parse if it's a string
      if (typeof responseData === "string") {
        try {
          parsedData = JSON.parse(responseData);
        } catch (e) {
          // If it's not JSON, treat as plain text
          sections.push(
            createLayoutBasedSection(sectionTitle, responseData, layoutType)
          );
          return sections;
        }
      } else {
        parsedData = responseData;
      }

      // Handle array format with columnTitle and content properties
      if (
        Array.isArray(parsedData) &&
        parsedData.length > 0 &&
        parsedData[0].columnTitle
      ) {
        sections.push(
          createLayoutBasedSection(sectionTitle, parsedData, layoutType)
        );
        return sections;
      }

      // Handle format 1: data: data: {"component_name": "...", "value": "..."}
      if (parsedData?.data?.data) {
        const innerData =
          typeof parsedData.data.data === "string"
            ? JSON.parse(parsedData.data.data)
            : parsedData.data.data;

        if (innerData.component_name && innerData.value) {
          // Use the API component name as title if no user title provided, otherwise use user title
          const finalTitle =
            userTitle && userTitle.trim()
              ? userTitle.trim()
              : innerData.component_name;

          if (typeof innerData.value === "string") {
            sections.push(
              createLayoutBasedSection(finalTitle, innerData.value, layoutType)
            );
          } else if (Array.isArray(innerData.value)) {
            sections.push(
              createLayoutBasedSection(finalTitle, innerData.value, layoutType)
            );
          } else if (typeof innerData.value === "object") {
            sections.push(
              createLayoutBasedSection(finalTitle, innerData.value, layoutType)
            );
          }
        }
      }
      // Handle format 2/3/4: {"component_name": "...", "value": ...}
      else if (parsedData?.component_name && parsedData?.value) {
        // Use the API component name as title if no user title provided, otherwise use user title
        const finalTitle =
          userTitle && userTitle.trim()
            ? userTitle.trim()
            : parsedData.component_name;

        if (typeof parsedData.value === "string") {
          sections.push(
            createLayoutBasedSection(finalTitle, parsedData.value, layoutType)
          );
        } else if (Array.isArray(parsedData.value)) {
          // If it's an array of objects with title/content, treat as card section
          sections.push(
            createLayoutBasedSection(finalTitle, parsedData.value, layoutType)
          );
        } else if (typeof parsedData.value === "object") {
          sections.push(
            createLayoutBasedSection(finalTitle, parsedData.value, layoutType)
          );
        }
      }
      // Handle direct object with columns
      else if (parsedData?.column1 || parsedData?.column2) {
        sections.push(
          createLayoutBasedSection(sectionTitle, parsedData, layoutType)
        );
      }
      // Handle array of cards
      else if (Array.isArray(parsedData)) {
        sections.push(
          createLayoutBasedSection(sectionTitle, parsedData, layoutType)
        );
      }
      // Fallback: treat as plain text
      else {
        sections.push(
          createLayoutBasedSection(
            sectionTitle,
            JSON.stringify(parsedData, null, 2),
            layoutType
          )
        );
      }
    } catch (error) {
      console.error("Error parsing response:", error);
      // Fallback to plain text using user-provided title
      sections.push(
        createLayoutBasedSection(
          sectionTitle,
          typeof responseData === "string"
            ? responseData
            : JSON.stringify(responseData, null, 2),
          layoutType
        )
      );
    }

    return sections;
  };

  const createLayoutBasedSection = (title, content, layoutType) => {
    // Create a section with layout type information
    return {
      type: "layout_section",
      layoutType: layoutType,
      title: title,
      content: content,
      component: () => {
        if (layoutType === "card_layout") {
          return createCardLayoutComponent(title, content);
        } else {
          return createNormalLayoutComponent(title, content, layoutType);
        }
      },
    };
  };

  const createCardLayoutComponent = (title, content) => {
    // Handle card layout display
    let cards = [];

    if (Array.isArray(content)) {
      cards = content;
    } else if (typeof content === "object" && content.cards) {
      cards = content.cards;
    } else if (typeof content === "object") {
      // Convert object to card format
      cards = Object.entries(content).map(([key, value]) => ({
        title: key,
        content:
          typeof value === "string" ? value : JSON.stringify(value, null, 2),
      }));
    } else {
      // Fallback: create a single card
      cards = [{ title: "Content", content: content }];
    }

    return (
      <div className="custom-section">
        <h3>{title}</h3>
        <div className="row">
          {cards.map((card, index) => (
            <div key={index} className="col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  {card.cardTitle && (
                    <h5 className="card-title">{card.cardTitle}</h5>
                  )}
                  <div
                    className="card-text"
                    dangerouslySetInnerHTML={{
                      __html: Array.isArray(card.content)
                        ? card.content
                            .map((item) =>
                              typeof item === "string"
                                ? item.replace(/\n/g, "<br/>")
                                : JSON.stringify(item, null, 2)
                            )
                            .join("<br/>")
                        : typeof card.content === "string"
                        ? card.content.replace(/\n/g, "<br/>")
                        : JSON.stringify(card.content, null, 2),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const createNormalLayoutComponent = (title, content, layoutType) => {
    // Handle normal layout display (one_column, two_columns, etc.)

    // Check if content is an array with columnTitle and content properties
    if (
      Array.isArray(content) &&
      content.length > 0 &&
      content[0].columnTitle
    ) {
      // Determine column classes based on number of items
      const getColumnClass = (totalItems) => {
        switch (totalItems) {
          case 1:
            return "col-12";
          case 2:
            return "col-md-6";
          case 3:
            return "col-md-4";
          case 4:
            return "col-md-6 col-lg-3";
          case 5:
            return "col-md-6 col-lg-4";
          case 6:
            return "col-md-6 col-lg-4";
          default:
            return "col-md-6 col-lg-4";
        }
      };

      return (
        <div className="custom-section">
          <h3>{title}</h3>
          <div className="row">
            {content.map((item, index) => (
              <div
                key={index}
                className={`${getColumnClass(content.length)} mb-3`}
              >
                <div className="card h-100">
                  <div className="card-header">
                    <h5 className="card-title mb-0">{item.columnTitle}</h5>
                  </div>
                  <div className="card-body">
                    <div
                      className="card-text"
                      dangerouslySetInnerHTML={{
                        __html: Array.isArray(item.content)
                          ? item.content
                              .map((val) =>
                                typeof val === "string"
                                  ? val.replace(/\n/g, "<br/>")
                                  : JSON.stringify(val, null, 2)
                              )
                              .join("<br/>")
                          : typeof item.content === "string"
                          ? item.content.replace(/\n/g, "<br/>")
                          : JSON.stringify(item.content, null, 2),
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Default normal layout
    return (
      <div className="custom-section">
        <h3>{title}</h3>
        <div className="custom-content">
          <div
            dangerouslySetInnerHTML={{
              __html:
                typeof content === "string"
                  ? content.replace(/\n/g, "<br/>")
                  : JSON.stringify(content, null, 2),
            }}
          />
        </div>
      </div>
    );
  };

  const renderResponsePreview = () => {
    const sections = parseResponseToSections(
      responseData,
      formData.layout_type
    );

    return (
      <div className="">
        <div className="preview-header">
          <h4>Generated Content Preview</h4>
          <p className="text-muted">
            Review the generated content before adding to document
          </p>
        </div>
        <div className="preview-content">
          {sections.map((section, index) => (
            <div key={index} className="preview-section mb-4">
              <div className="section-header">
                <h5 className="section-title">{section.title}</h5>
                <span className="section-title">
                  {section.layoutType || section.type}
                </span>
              </div>
              <div className="section-content">
                {section.type === "layout_section" && (
                  <div>{section.component()}</div>
                )}
                {section.type === "text_section" && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: (typeof section.content === 'string' 
                        ? section.content 
                        : Array.isArray(section.content) 
                          ? section.content.join(' ')
                          : String(section.content || '')
                      ).replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
                {section.type === "multi_column_section" && (
                  <div className="row">
                    {section.columns.map((col, colIndex) => (
                      <div key={colIndex} className="col-md-6  mb-3">
                        <div className="card h-100">
                          <div className="card-body">
                            <h5 className="card-title">{col.title}</h5>
                            <div
                              className="card-text"
                              dangerouslySetInnerHTML={{
                                __html: col.content.replace(/\n/g, "<br/>"),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {section.type === "card_section" && (
                  <div className="row">
                    {section.cards.map((card, cardIndex) => (
                      <div key={cardIndex} className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body">
                            {/* Show all fields in the card */}
                            {Object.entries(card).map(([key, value]) => (
                              <div key={key} className="mb-2">
                                <strong>{key}:</strong>{" "}
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      typeof value === "string"
                                        ? value.replace(/\n/g, "<br/>")
                                        : JSON.stringify(value, null, 2),
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-between gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleBackToForm}
          >
            Back to Form
          </button>
          <button
            type="button"
            className="btn btn-dark"
            onClick={handleAddToDocument}
          >
            Add{" "}
            {sections.length > 1 ? `${sections.length} Sections` : "Section"} to
            Document
          </button>
        </div>
      </div>
    );
  };

  const handleCloseModal = () => {
    setShowCustomModal(false);
    setShowResponse(false);
    setResponseData(null);
    // Reset form data
    setFormData({
      title: "",
      layout_type: "",
      column1: null,
      column2: null,
      column3: null,
      column4: null,
      column5: null,
      column6: null,
      card: null,
    });
  };

  const handleBackToForm = () => {
    setShowResponse(false);
    setResponseData(null);
  };

  const renderColumnSection = (columnKey, columnData, title) => (
    <div className="">
      <div className="card h-100">
        <div className="card-header ">
          <h6 className="mb-0">{title}</h6>
        </div>
        <div className="">
          <div className="mb-3">
            <label className="form-label">Output</label>
            <select
              className="form-select"
              value={columnData?.output || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  [columnKey]: {
                    ...prev[columnKey],
                    output: e.target.value,
                  },
                }))
              }
            >
              <option value="">Select type...</option>
              <option value="text">Text</option>
              <option value="image">Image</option>
            </select>
          </div>
          {columnData?.output === "text" && (
            <div className="mt-3">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Goal</label>
                  <textarea
                    className="form-control"
                    value={columnData?.goal || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [columnKey]: {
                          ...prev[columnKey],
                          goal: e.target.value,
                        },
                      }))
                    }
                    placeholder="Enter your goal..."
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Constraint</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={columnData?.constraint || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [columnKey]: {
                          ...prev[columnKey],
                          constraint: e.target.value,
                        },
                      }))
                    }
                    placeholder="Enter constraints..."
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Further Consideration</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={columnData?.further_consideration || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [columnKey]: {
                          ...prev[columnKey],
                          further_consideration: e.target.value,
                        },
                      }))
                    }
                    placeholder="Enter further considerations..."
                  />
                </div>
              </div>
            </div>
          )}
          {columnData?.output === "image" && (
            <div className="text-center text-muted py-4">
              <i className="bi bi-image fs-1"></i>
              <p className="mb-0">Image content selected</p>
              <small>No additional inputs required</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleAddSection = (section) => {
    onAddSection(section);
    setIsModalOpen(false);
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6">
          <div className="empty-section">
            <div className="placeholder-icon">
              <img
                src="/addsection.svg"
                alt="Add Section"
                className="img-fluid w-200 h-200"
              />
            </div>
            <h3 className="empty-title">Add more sections</h3>
            <p className="empty-description">
              Showcasing product benefits can help you increase sales. We have a
              variety of components for you to select from.
            </p>
            <button
              className="add-section-btn"
              onClick={() => setIsModalOpen(true)}
            >
              Add Section
            </button>
          </div>
        </div>
      </div>
      <SectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddSection={handleAddSection}
        previewMode={previewMode}
        openCustomModal={() => {
          setIsModalOpen(false);
          setShowCustomModal(true);
        }}
        initialSku={initialSku}
        keywords={keywords}
        agentId={agentId}
        sessionId={sessionId}
        language={language}
        locationFocus={locationFocus}
        furtherDetails={furtherDetails}
      />
      {showCustomModal && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
        >
          <div
            className="section-modal"
            style={{ maxWidth: 700, width: "100%" }}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                {showResponse
                  ? "Custom Section Generated"
                  : "Add Custom Section"}
              </h3>
              <button className="close-button" onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <div className="custom-section-content">
              {!showResponse ? (
                <form onSubmit={handleFormSubmit}>
                  {/* Title Input */}
                  <div className="mb-4">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Enter form title..."
                      required
                    />
                  </div>
                  {/* Layout Type Dropdown */}
                  <div className="mb-4">
                    <label className="form-label">Layout Type</label>
                    <select
                      className="form-select"
                      value={formData.layout_type}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          layout_type: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Select layout type...</option>
                      <option value="one_column">One Column</option>
                      <option value="two_columns">Two Columns</option>
                      <option value="three_columns">Three Columns</option>
                      <option value="four_columns">Four Columns</option>
                      <option value="five_columns">Five Columns</option>
                      <option value="six_columns">Six Columns</option>
                      <option value="card_layout">Card Layout</option>
                    </select>
                  </div>
                  {/* Dynamic Content Based on Layout Type */}
                  {[
                    "one_column",
                    "two_columns",
                    "three_columns",
                    "four_columns",
                    "five_columns",
                    "six_columns",
                  ].includes(formData.layout_type) && (
                    <>
                      {/* <div>Rendering {layoutTypeToCount[formData.layout_type]} columns</div> */}
                      <div className="row">
                        {Array.from(
                          { length: layoutTypeToCount[formData.layout_type] },
                          (_, idx) => {
                            const colKey = `column${idx + 1}`;
                            return (
                              <div className="col-12 mb-3" key={colKey}>
                                {renderColumnSection(
                                  colKey,
                                  formData[colKey],
                                  `Column ${idx + 1}`
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </>
                  )}
                  {/* Card Layout */}
                  {formData.layout_type === "card_layout" && (
                    <div className="card">
                      <div className="card-header ">
                        <h6 className="mb-0">Card Content</h6>
                      </div>
                      <div className="">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Output
                          </label>
                          <select
                            className="form-select"
                            value={formData.card?.output || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                card: {
                                  ...prev.card,
                                  output: e.target.value,
                                },
                              }))
                            }
                          >
                            <option value="">Select type...</option>
                            <option value="text">Text</option>
                            <option value="image">Image</option>
                          </select>
                        </div>
                        {formData.card?.output === "text" && (
                          <div className="mt-4">
                            <div className="row g-3">
                              <div className="col-12">
                                <div className="mb-4">
                                  <label className="form-label">
                                    Card Count
                                  </label>
                                  <select
                                    className="form-select"
                                    value={formData.card?.card_count || ""}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        card: {
                                          ...prev.card,
                                          card_count: e.target.value,
                                        },
                                      }))
                                    }
                                  >
                                    <option value="">
                                      Select card count...
                                    </option>
                                    <option value="1">One card</option>
                                    <option value="2">Two cards</option>
                                    <option value="3">Three cards</option>
                                    <option value="4">Four cards</option>
                                    <option value="5">Five cards</option>
                                    <option value="6">Six cards</option>
                                  </select>
                                </div>
                                <label className="form-label">Goal</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={formData.card?.goal || ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      card: {
                                        ...prev.card,
                                        goal: e.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Enter your goal..."
                                />
                              </div>
                              <div className="col-12">
                                <label className="form-label">Constraint</label>
                                <textarea
                                  className="form-control"
                                  rows="3"
                                  value={formData.card?.constraint || ""}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      card: {
                                        ...prev.card,
                                        constraint: e.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Enter constraints..."
                                />
                              </div>
                              <div className="col-12">
                                <label className="form-label">
                                  Further Consideration
                                </label>
                                <textarea
                                  className="form-control"
                                  rows="3"
                                  value={
                                    formData.card?.further_consideration || ""
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      card: {
                                        ...prev.card,
                                        further_consideration: e.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Enter further considerations..."
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {formData.card?.output === "image" && (
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-image fs-1"></i>
                            <p className="mb-0">Image content selected</p>
                            <small>No additional inputs required</small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Submit Button */}
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                      type="submit"
                      className="btn btn-dark"
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Generating...
                        </>
                      ) : (
                        "Generate"
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                renderResponsePreview()
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSection;
